import React from 'react';
import PropTypes from 'prop-types';
import trim from 'lodash/trim';
import { withRouter } from 'react-router';
import { getBrowserHistoryPathname } from '../../../shared/utils/win';
import { Link } from '../Link/Link';
import { getContextId } from '~shared/utils/uriAliases';
import { getBrandDefaultLanguage } from '~spa/SpectateConfig';
import { getLanguageCode } from '~shared/utils/user';
import { getRewriteUrlLang } from '~shared/locales/utils/utils';

/**
 * This class is in replacement of react-router's one.
 * The reason is that we are now using two history objects,
 * the browser one, to handle the localized urls to be displayed
 * to the users, and the memory one, to keep our internal routing
 * the same as before (ie: without having to deal with localization).
 * The issue is that react-router Link component will interact
 * with the history that is passed to <Router>, which in our case
 * is the memory history. The result is that when clicking the links
 * the SPA would update, but not the browser history.
 *
 * This NavLink component is using our custom Link to
 * call navigateTo() to update the browser history.
 *
 * @returns {Element} The NavLinkComponent component
 */
const NavLinkComponent = ({ to, children, className, activeClassName, gtmAttribute}) => {
    let classNames = className;

    const userLang = getLanguageCode();
    const defaultLang = getBrandDefaultLanguage();

    let pathname = getBrowserHistoryPathname();

    if (userLang !== defaultLang) {
        const langPrefix = `${getRewriteUrlLang(userLang)}/`;
        pathname = pathname.replace(langPrefix, '/');
    }

    if (getContextId(pathname) === getContextId(to)) {
        classNames = trim(`${classNames} ${activeClassName}`);
    }

    return (
        <Link to={to} key={to} className={classNames} gtmAttribute={gtmAttribute}>
            {children}
        </Link>
    );
};

NavLinkComponent.propTypes = {
    to: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    activeClassName: PropTypes.string,
    gtmAttribute: PropTypes.string,
};

NavLinkComponent.defaultProps = {
    className: null,
    activeClassName: 'active',
};

export const NavLink = withRouter(NavLinkComponent);
